import { useEffect, useState } from 'react';
import { useSiteStore } from '@/store/site';
import { useAuthActions, useAuthToken } from '@/store/auth';
import { getResponseJwtHeader } from '@/utils/web';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { useExternalScript } from '@nf/utils-common/hooks';
import { cloudUrlParser } from '@/utils/aws-domain-util';
import { Maybe } from '@nf/utils-common/fpw/monadic';

export interface FpsRequest {
	di: string;
	bi: string;
	fdi: string;
	analysis: string;
}

export const useFps = (
	AgentSite: string | undefined,
	EnableFps: boolean | undefined,
	FpsJsUrl: string | undefined,
) => {
	const [hasCheckFps, setHasCheckFps] = useState(false);
	const [apiDomain] = useSiteStore(state => [state.apiDomain]);
	const { updateAuthToken } = useAuthActions();
	const authToken = useAuthToken();
	const fpsJsUrl = EnableFps ? FpsJsUrl : '';
	const getExternalJs = useExternalScript(fpsJsUrl ?? '');

	const fetchFpsHandler = async ({ di, bi, fdi, analysis }: FpsRequest) => {
		try {
			const formData = new FormData();
			formData.append('__di', di);
			formData.append('__bi', bi);
			formData.append('__fdi', fdi);
			formData.append('detecas-analysis', analysis);

			const options = {
				body: formData
			};

			const response = await fetchComposer.postWithBearerBase(cloudUrlParser(`${apiDomain.ctcdDomain}/FpsHandler`), options)(authToken);

			if (!response.ok) {
				throw new Error(`${response.status}: ${response.statusText}`);
			}

			Maybe(getResponseJwtHeader(response)).fold(() => {}, updateAuthToken);
		} catch (error) {
			console.log(error);
		}
	};

	const checkFps = (count: number) => {
		const __di = document.getElementById('__di') as HTMLInputElement | null;
		const __bi = document.getElementById('__bi') as HTMLInputElement | null;
		const __fdi = document.getElementById('__fdi') as HTMLInputElement | null;
		const __analysis = document.getElementById('detecas-analysis') as HTMLInputElement | null;

		if (__di != null || __bi != null || __fdi != null || __analysis != null) {
			fetchFpsHandler({
				di: __di?.value ?? '',
				bi: __bi?.value ?? '',
				fdi: __fdi?.value ?? '',
				analysis: __analysis?.value ?? ''
			});
		} else {
			if (count < 10) setTimeout(() => {
				checkFps(count + 1);
			}, 1000);
		}
	}
	// In a new incognito window, logging in via a deep link with a token in the query string causes the entire page to re-render. 
	// To prevent the FPS handler from being triggered twice, implement a timer.
	useEffect(() => {
		const timer = setTimeout(() => {
			if (
				!EnableFps || 
				!AgentSite || 
				hasCheckFps || 
				['12bet', 'soon88'].includes(AgentSite.toLowerCase())
			) {
				return;
			}
	
			switch (getExternalJs) {
				case 'error':
					fetchFpsHandler({
						di: '',
						bi: '',
						fdi: '',
						analysis: ''
					});
					setHasCheckFps(true);
					return;
				case 'ready':
					checkFps(0);
					setHasCheckFps(true);
					return;
				case 'idle':
				case 'loading':
				default:
					return;
			}
		}, 2000); 
	
		return () => clearTimeout(timer); 
	}, [apiDomain.ctcdDomain, getExternalJs, hasCheckFps, AgentSite, EnableFps]);
	
};
